import { SearchConfigService } from './../../services/search/search-config.service';
import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Endpoints } from '../../../config/endpoints.config';
import { DiskService } from '../../services/disk/disk.service';
import { CustomerSampleAllocationDTO, SampleAllocationDTO, LotDTO } from '../../models/sample-model';
import { AuthenticationService } from '../../services/authentication.service';
import { DB_SYNC_STATE_KEYS, DB_KEY_PREFIXES } from '../../config/pouch-db.config';
import { DeviceService } from '../../services/device/device.service';
import { SampleService } from '../../services/sample/sample.service';
import { CreateSampleDropRequestBody, SampleActivity } from '../../classes/activity/sample.activity.class';
import { ActivityService } from '../../services/activity/activity.service';

import { Events } from '@omni/events';
import { FeatureActionsMap, IoConfigurationFeatureCodes } from '../../classes/authentication/user.class';
import { NotificationService, ToastStyle } from '../../services/notification/notification.service';
import { DeltaService, EntityNames, EntitySyncInfo } from '../delta/delta.service';
import { UIService } from '../../services/ui/ui.service';
import { TranslateService } from '@ngx-translate/core';
import { CustomerLicenseDTO, CUSTOMER_LICENSE_TYPE, isTDDDLicenseType } from '../../classes/sampling/customer-license.class';
import { GlobalUtilityService } from '../../services/global-utility.service';
import { Contact } from '../../classes/contact/contact.class';
import { SyncFeatureCategory } from '../../enums/delta-service/delta-service.enum';
import { AllocationAdjustService } from '../../services/sample/allocation-adjust.service';
import { Utility } from '@omni/utility/util';
import { fetchQueries } from '@omni/config/dynamics-fetchQueries';
import { DynamicsClientService } from '../dynamics-client/dynamics-client.service';
import { Position } from '@omni/classes/account/child.position.class';
import { count } from 'console';
import { getBucketId } from '@omni/services/contact/contact.service';
import { AllocationProductEligibility, CustomerSampleProductEligibility } from '@omni/classes/sample/customer-sample-allocation.class';
import { CaseManagementService } from '@omni/services/case-management/case-management.service';
import { SyncProfilerService } from '@omni/services/logging/sync-profiler.service';
import { concat, isEmpty, uniqBy } from 'lodash-es';
import { DateTimeFormatsService } from '@omni/services/date-time-formats/date-time-formats.service';

@Injectable({
  providedIn: 'root'
})
export class SampleDataService {
    private _newDeltaSampleAllocIds: Array<string> = [];
    private _newDeltaCustSampleAllocIds: { addedOrUpdated: string[], deleted: Array<{ id: string, contactId: string }> } = { addedOrUpdated: [], deleted: [] };
    private _newDeltaLotIds: Array<string> = [];
    private _isInitialMappingDone: boolean = false;
    private _isInitialCustomerAllocationMappingDone: boolean = false;
    private _customerLicenseFetchCache = new Map<string, { promise: Promise<any[]>; expires: number }>();
    private static readonly CUSTOMER_LICENSE_FETCH_TTL_MS = 30_000;
    private eligibleCustomerLicenseContactIds = new Set<string>();
    private _syncLotsFetchCache = new Map<string, { promise: Promise<void>; expires: number }>();
    private static readonly SYNC_LOTS_DEDUP_TTL_MS = 5_000;
    private _sampleDropDetailsFetchCache = new Map<string, { promise: Promise<any | null>; expires: number }>();
    private static readonly SAMPLE_DROP_DETAILS_DEDUP_TTL_MS = 5_000;


    constructor(
        private http: HttpClient,
        private disk: DiskService,
        private deviceService: DeviceService,
        private authenticationService: AuthenticationService,
        private sampleService: SampleService,
        public activityService: ActivityService,
        public events: Events,
        public notificationService: NotificationService,
        private deltaService: DeltaService,
        private uiService: UIService,
        public searchConfigService: SearchConfigService,
        public translate:TranslateService,
        private globalUtility: GlobalUtilityService,
        private dynamics: DynamicsClientService,
        private adjustService: AllocationAdjustService,
        private injector: Injector,
        private readonly syncProfilerService: SyncProfilerService,
        private dateTimeFormatsService: DateTimeFormatsService,
    ) {
    }

    async syncSamples(forceFullSync = false, loadFromDbOnly = false, profilerParentSpanId?: string) {
        // Fetch from backend and save to DB
        //await this._fetchAndSaveSampleAllocations(forceFullSync);
        if(!this.authenticationService.hasFeatureAction(FeatureActionsMap.ALLOCATION_TOOL)){
            return
        }
        this.deltaService.pushSyncEntityName(SyncFeatureCategory.orders);
        this.fetchSKUSampleUnitPrices(loadFromDbOnly);
        await this.syncProfilerService.timeAsync('samples_eligibilities', () => Promise.all([
          this.fetchAllocationProductEligibilities(loadFromDbOnly),
          this.fetchCustomerSampleProductEligibilities(forceFullSync, loadFromDbOnly),
        ]), profilerParentSpanId);
        await this.syncProfilerService.timeAsync('samples_customer_allocations', () => this._fetchAndSaveCustomerSampleAllocations(forceFullSync, loadFromDbOnly), profilerParentSpanId);
        if (this.authenticationService.hasFeatureAction(FeatureActionsMap.ALLOCATION_ORDERS_UNRESTRICTED)) {
          await this.syncProfilerService.timeAsync('samples_max_order_qty', () => this.fetchAllocationsMaxOrderQuantity(undefined, loadFromDbOnly), profilerParentSpanId);
        }


        // Load all from DB. Runs only once when app is loading.
        //await this.sampleService.loadAllSampleAndCustomerAllocationsFromDBAndMap(forceFullSync);
        //await this.sampleService.loadAllContactCustomerAllocationsFromDBAndMap(forceFullSync);

        // Load newly saved delta from DB.
        //await this.sampleService.loadAndMapNewDeltaToSampleAndCustomerAllocations(this._newDeltaSampleAllocIds, this._newDeltaCustSampleAllocIds);
        // await this.sampleService.loadAndMapNewDeltaToContactCustomerAllocations(this._newDeltaCustSampleAllocIds);

        //this._newDeltaSampleAllocIds = [];
        this._newDeltaCustSampleAllocIds = { addedOrUpdated: [], deleted: [] };
        this.searchConfigService.isAllocationsMappedToContacts = false;
    }

    private fetchSKUSampleUnitPrices(loadFromDBOnly) {
      if (loadFromDBOnly || this.deviceService.isOffline) {
        this.sampleService.loadSKUSampleUnitPrice();
        return;
      };
      let fetchXML = fetchQueries.sampleUnitPricing;
      let positionString = '';
      this.authenticationService.user.positions.forEach(({ ID }) => positionString += `<value>${ID}</value>`);
      fetchXML = fetchXML.split('{positionIds}').join(positionString);
      this.dynamics.executeFetchQuery('products', fetchXML).then(resp => {
        this.sampleService.saveSKUSampleUnitPrice(resp);
      }).catch((error) => {
        console.error("Failed to fetch fetchSKUSampleUnitPrices: ", error);
      });
    }

    public async fetchAllocationsMaxOrderQuantity(sampleProductIds?: string[], loadFromDbOnly = false) : Promise<{ sampleProductId: string, maxOrderQuantity: number }[]> {
      if (!this.authenticationService.hasFeatureAction(FeatureActionsMap.ALLOCATION_ORDERS_UNRESTRICTED)) {
        return [];
      }
      const requestSource = Array.isArray(sampleProductIds) && sampleProductIds.length > 0 ? 'direct-selection' : 'prefetch-derived';
      let targetSampleProductIds = sampleProductIds || [];
      if (!targetSampleProductIds.length) {
        const caseManagementService = this.injector.get(CaseManagementService, null);
        const caseProductSKU = Array.isArray(caseManagementService?.caseProductSKU) ? caseManagementService.caseProductSKU : [];
        const skuIdsFromCaseProducts = caseProductSKU.flatMap(product => Array.isArray(product?.skus) ? product.skus : []).map(sku => sku?.ID).filter(Boolean);
        targetSampleProductIds = Array.from(new Set(skuIdsFromCaseProducts));
      } else {
        targetSampleProductIds = Array.from(new Set(targetSampleProductIds.filter(Boolean)));
      }
      console.log('[ALLOC_MAX_QTY_DEBUG][fetchAllocationsMaxOrderQuantity][input]', {
        requestSource,
        requestedSampleProductIds: sampleProductIds || [],
        resolvedSampleProductIds: targetSampleProductIds,
      });

      if (targetSampleProductIds.length === 0) {
        return [];
      }

      const uniqueEligiblePreoductsbyModifiedOn = (
        rows: Array<{ sampleProductId: string, maxOrderQuantity: number, modifiedOn?: string }>
      ): Array<{ sampleProductId: string, maxOrderQuantity: number }> => {
        const uniqueBySku = new Map<string, { sampleProductId: string, maxOrderQuantity: number, modifiedOn?: string }>();
        rows.forEach(row => {
          if (!row?.sampleProductId || row.maxOrderQuantity === undefined || row.maxOrderQuantity === null) {
            return;
          }
          const existing = uniqueBySku.get(row.sampleProductId);
          if (!existing) {
            uniqueBySku.set(row.sampleProductId, row);
            return;
          }
          const currentTime = row.modifiedOn ? new Date(row.modifiedOn).getTime() : Number.NEGATIVE_INFINITY;
          const existingTime = existing.modifiedOn ? new Date(existing.modifiedOn).getTime() : Number.NEGATIVE_INFINITY;
          if (currentTime > existingTime) {
            uniqueBySku.set(row.sampleProductId, row);
          }
        });
        return Array.from(uniqueBySku.values()).map(({ sampleProductId, maxOrderQuantity }) => ({
          sampleProductId,
          maxOrderQuantity
        }));
      };

      const loadFromLocal = async () => {
        const doc = await this.disk.retrieve(DB_KEY_PREFIXES.ALLOCATION_MAX_ORDER_QUANTITY);
        const cached = Array.isArray(doc?.raw) ? doc.raw : [];
        const filtered = cached.filter(item => targetSampleProductIds.includes(item.sampleProductId));
        return uniqueEligiblePreoductsbyModifiedOn(filtered);
      };

      if (this.deviceService.isOffline || loadFromDbOnly) {
        return loadFromLocal();
      }

      let sampleProductIdsString = '';
      targetSampleProductIds.forEach(item => {
        sampleProductIdsString += `<value>${item}</value>`;
      });
      const fetchXML = fetchQueries.allocationsMaxOrderQuantity.split('{sampleProductIds}').join(sampleProductIdsString);
      return this.dynamics.executeFetchQuery('indskr_sampleproductdropconfigurations', fetchXML).then(async (maxOrderQuantity) => {
        const mappedRows = (maxOrderQuantity || [])
          .map(item => ({
            sampleProductId: item._indskr_sampleproductid_value,
            maxOrderQuantity: item.indskr_samplelimitpervisit,
            modifiedOn: item.modifiedon
          }))
          .filter(item => item.sampleProductId && item.maxOrderQuantity !== undefined && item.maxOrderQuantity !== null);
        const response = uniqueEligiblePreoductsbyModifiedOn(mappedRows);
        await this.disk.updateOrInsert(DB_KEY_PREFIXES.ALLOCATION_MAX_ORDER_QUANTITY, doc => ({ raw: response }));
        return response;
      }).catch((error) => {
        console.error("Failed to fetch fetchAllocationsMaxOrderQuantity: ", error);
        return loadFromLocal();
      });
    }

    private async fetchAllocationProductEligibilities(loadFromDbOnly = false) {
      if (!(this.deviceService.isOffline || loadFromDbOnly)) {
        let positionString = '';
        this.authenticationService.user.positions.forEach(({ ID }) => positionString += `<value>${ID}</value>`);
        const fetchXML = fetchQueries.allocationProductEligibilities.split('{positionIds}').join(positionString);
        await this.dynamics.executeFetchQuery('products', fetchXML).then((eligibilities: AllocationProductEligibility[]) => {
          this.sampleService.saveAllocationEligibilities(eligibilities);
        }).catch((error) => {
          console.error("Failed to fetch fetchSKUSampleUnitPrices: ", error);
        });
      } else {
        await this.sampleService.loadAllocationProductEligibilities();
      }
    }

    private async fetchCustomerSampleProductEligibilities(forceFullSync = false, loadFromDbOnly = false) {
      if (!(this.deviceService.isOffline || loadFromDbOnly)) {
        const syncState = await this.disk.getSyncState(DB_SYNC_STATE_KEYS.SYNC_CUST_SAMPLE_PROD_ELIGIBILITIES);
        const startDate = this.dateTimeFormatsService.formatDate(new Date(), 'YYYY-MM-DD');
        let fetchXML = fetchQueries.customerSampleProductEligibilities.split('{date}').join(startDate);
        const isInitialSync = forceFullSync || !syncState || !syncState.lastUpdatedTime ? true : new Date(syncState.lastUpdatedTime).getMonth() != new Date(startDate).getMonth();
        let positionString = '';
        this.authenticationService.user.positions.forEach(({ ID }) => positionString += `<value>${ID}</value>`);
        fetchXML = fetchXML.split('{positionIds}').join(positionString);
        if (isInitialSync) {
          fetchXML = fetchXML.replace('{deltaSyncFilter}', '');
        } else {
          const deltaSyncFilter = '<filter type="and"><condition attribute="modifiedon" operator="eq" value="{modifedon}" /></filter>'
          .replace('{modifedon}', this.dateTimeFormatsService.formatDate(new Date(syncState.lastUpdatedTime), 'YYYY-MM-DD'));
          fetchXML = fetchXML.replace('{deltaSyncFilter}', deltaSyncFilter);
        }
        await this.dynamics.executeFetchQuery('indskr_customersampleproducteligibilities', fetchXML).then(async (customerSampleProductEligibilities: CustomerSampleProductEligibility[]) => {
          this.sampleService.saveCustomerSampleProductEligibilities(customerSampleProductEligibilities, isInitialSync);
          syncState.lastUpdatedTime = new Date().getTime();
          await this.disk.updateSyncState(syncState);
        }).catch((error) => {
          console.error("Failed to fetch CustomerSampleProductEligibilities: ", error);
        });
      } else {
        await this.sampleService.loadCustomerSampleProductEligibilities();
      }
    }

    async syncIndividualCustomerAllocationsFromContactProfile(rawAllocations:CustomerSampleAllocationDTO[]){
        this._newDeltaCustSampleAllocIds = { addedOrUpdated: [], deleted: [] };
        await this.sampleService.saveDeltaSyncedCustomerSampleAllocations(rawAllocations, this._newDeltaCustSampleAllocIds);
        await this.sampleService.loadAndMapNewDeltaToContactCustomerAllocations(this._newDeltaCustSampleAllocIds, true, rawAllocations);
        this._newDeltaCustSampleAllocIds = { addedOrUpdated: [], deleted: [] };
        this.events.publish('Contact-Profile:refreshContactSampleEligibilities')
    }

  private async _fetchAndSaveCustomerSampleAllocations(forceFullSync = false, loadFromDbOnly = false) {

    if (!(this.deviceService.isOffline || loadFromDbOnly)) {

      let syncStatesFromDB = await this.disk.getSyncState(DB_SYNC_STATE_KEYS.SYNC_CUST_SAMPLE_ALLOC);

      let syncStates = syncStatesFromDB.syncStates

      const startDate = new Date().getTime();
      const endDate = new Date(this.dateTimeFormatsService.formatDate(this.dateTimeFormatsService.getEndOfMonth(this.dateTimeFormatsService.addMonthsToDate(new Date(), 1)), 'YYYY-MM-DD')).getTime();

      let mypositions = this.authenticationService.user.positions.map(position => position.ID)
      const childPositions = this.authenticationService.user.childPositions.map(position => position.childPositionId)

      if (childPositions && !isEmpty(childPositions)) {
        mypositions = mypositions.concat(childPositions)
      }

      const allocationSyncInfo: EntitySyncInfo = {
        entityName: EntityNames.sampleProduct,
        totalFailed: 0,
        totalSynced: 0,
        errors: [],
        syncStatus: true
      };

      let errors = []

      let positionWiseResponses = {}

      if (mypositions.length > 0) {

        await this.syncProfilerService.timeAsync('samples_alloc_api', async () => {
          for (let positionIndex = 0; positionIndex < mypositions.length; positionIndex++) {
            const position = mypositions[positionIndex];

            let syncState

            const newLastUpdatedTime = new Date().getTime();

            if (syncStates && syncStates.length > 0) {
              syncState = syncStates.find(syncState => syncState.positionID == position)
            }

            const isInitialSync = (!syncStatesFromDB.syncStates || isEmpty(syncStatesFromDB.syncStates)) || (!syncState || (syncState && syncState.lastUpdatedTime ? new Date(syncState.lastUpdatedTime).getMonth() != new Date(newLastUpdatedTime).getMonth() : !syncState.lastUpdatedTime))

            const doFullSync = forceFullSync || isInitialSync;

            let url = this.authenticationService.userConfig.activeInstance.entryPointUrl + Endpoints.sample.CUSTOMER_SAMPLE_ALLOCATIONS.replace('{{positionIDs}}', position)
              .replace('{{startDate}}', startDate.toString())
              .replace('{{endDate}}', endDate.toString());

            url = !doFullSync ? url + '&lastUpdatedTime=' + syncState.lastUpdatedTime : url;

            let sampleUnitPriceForSKUs;

            try {
              let response = await this.http.get<CustomerSampleAllocationDTO[]>(url, Endpoints.GLOBAL_SYNC_HEADER).toPromise();
              syncStates = this.updateSyncState(allocationSyncInfo, syncState, newLastUpdatedTime, syncStates ? syncStates : [], position);
              if (response && Array.isArray(response)) {
                allocationSyncInfo.totalSynced += response.length
                // completeResponse =  concat(completeResponse, response)
                positionWiseResponses[position] = response
              }
            } catch (error) {
              console.error('_fetchAndSaveCustomerSampleAllocations: ', error);
              //allocationSyncInfo.errorMessage = '[sampleAllocation][error]' + error ? error.errorMessage : '';
              this.deltaService.addSyncErrorToEntitySyncInfo(allocationSyncInfo, url, error);
              syncStates = this.updateSyncState(allocationSyncInfo, syncState, newLastUpdatedTime, syncStates ? syncStates : [], position);
            }
          }
        });
      }

      //positionID-> response 
      //if there is no sync state in that case it's complete initial Sync 
      //1. Do compacting
      //2. save that as an initial sync if all the Sync States are empty 

      await this.syncProfilerService.timeAsync('samples_alloc_processing', async () => {
        let completeResponseArray: CustomerSampleAllocationDTO[] = [];

        mypositions.forEach(position => {
          completeResponseArray = completeResponseArray.concat(positionWiseResponses[position])
        })
        let completeUniqueResponseArray = uniqBy(completeResponseArray, 'indskr_customersampleproductid')

        if (!syncStatesFromDB.syncStates || isEmpty(syncStatesFromDB.syncStates)) {// Condition for Initial Sync
          await this.sampleService.saveFullSyncedCustomerSampleAllocations(completeUniqueResponseArray, forceFullSync);
          this._isInitialCustomerAllocationMappingDone = true;

        } else {
          if (!this._isInitialCustomerAllocationMappingDone) {
            await this.sampleService.loadAllContactCustomerAllocationsFromDBAndMap([], true);
            this._isInitialCustomerAllocationMappingDone = true;
          }
          if (!isEmpty(completeUniqueResponseArray)) {
            this._newDeltaCustSampleAllocIds = { addedOrUpdated: [], deleted: [] };
            await this.sampleService.saveDeltaSyncedCustomerSampleAllocations(completeUniqueResponseArray, this._newDeltaCustSampleAllocIds);
            await this.sampleService.loadAndMapNewDeltaToContactCustomerAllocations(this._newDeltaCustSampleAllocIds, false, completeResponseArray);
          }
        }
        syncStatesFromDB.syncStates = syncStates
        await this.disk.updateSyncState(syncStatesFromDB);
      });

      this.deltaService.addEntitySyncInfo(allocationSyncInfo);
    } else {
      await this.sampleService.loadAllContactCustomerAllocationsFromDBAndMap([], true);
    }
  }

  private updateSyncState(allocationSyncInfo: EntitySyncInfo, syncState: any, newLastUpdatedTime: number, syncStatesFromDB: any, positionID: string) {
    if (allocationSyncInfo.syncStatus) {

      if (!syncState) {syncState = {lastUpdatedTime: newLastUpdatedTime, positionID: positionID}}
      else syncState.lastUpdatedTime = newLastUpdatedTime;

      if (syncStatesFromDB && Array.isArray(syncStatesFromDB) && !isEmpty(syncStatesFromDB)){
        const index =  syncStatesFromDB.findIndex(syncState => syncState.positionID == positionID)
        if (index > -1) { syncStatesFromDB[index] = syncState; } else syncStatesFromDB.push(syncState);
      } else {
        syncStatesFromDB.push(syncState)
      }
    }
    return syncStatesFromDB;
  }

    async syncLots(dataRange: { from: string, to: string }, forceFullSync = false, loadFromDbOnly = false, profilerParentSpanId?: string): Promise<void> {
        if(!this.authenticationService.hasFeatureAction(FeatureActionsMap.ALLOCATION_TOOL)){
            return
        }
        const canDedupe = !loadFromDbOnly;
        const cacheKey = `${dataRange?.from || ''}::${dataRange?.to || ''}::${forceFullSync ? 1 : 0}`;
        if (canDedupe) {
            const cached = this._syncLotsFetchCache.get(cacheKey);
            if (cached && Date.now() < cached.expires) {
                return cached.promise;
            }
        }
        const promise = this._syncLotsImpl(dataRange, forceFullSync, loadFromDbOnly, profilerParentSpanId);
        if (canDedupe) {
            this._syncLotsFetchCache.set(cacheKey, {
                promise,
                expires: Date.now() + SampleDataService.SYNC_LOTS_DEDUP_TTL_MS,
            });
            promise.catch(() => this._syncLotsFetchCache.delete(cacheKey));
        }
        return promise;
    }

    private async _syncLotsImpl(dataRange: { from: string, to: string }, forceFullSync = false, loadFromDbOnly = false, profilerParentSpanId?: string): Promise<void> {
        // Fetch from backend and save to DB
        await this.syncProfilerService.timeAsync('sample_lots_fetch_save', () => this._fetchAndSaveLots(dataRange, forceFullSync, loadFromDbOnly), profilerParentSpanId);

        // Load all from DB. Runs only once when app is loading.
        await this.syncProfilerService.timeAsync('sample_lots_db_map', async () => {
          await this.sampleService.loadAllLotsFromDBAndMap(forceFullSync);
          await this.adjustService.loadAllLotsFromDBAndMap(forceFullSync);
        }, profilerParentSpanId);

        // Load newly saved delta from DB.
        // await this.sampleService.loadAndMapNewDeltaToLots(this._newDeltaLotIds);
        // await this.adjustService.loadAndMapNewDeltaToLots(this._newDeltaLotIds);

        this._newDeltaLotIds = [];
    }

    public async fetchSampleDropActivityOnline(activityId: string): Promise<any | null> {
        if (!activityId || this.deviceService.isOffline || activityId.includes('offline_')) {
            return null;
        }
        const cached = this._sampleDropDetailsFetchCache.get(activityId);
        if (cached && Date.now() < cached.expires) {
            return cached.promise;
        }
        const promise = this._fetchSampleDropActivityOnlineImpl(activityId);
        this._sampleDropDetailsFetchCache.set(activityId, {
            promise,
            expires: Date.now() + SampleDataService.SAMPLE_DROP_DETAILS_DEDUP_TTL_MS,
        });
        const clear = () => {
            if (this._sampleDropDetailsFetchCache.get(activityId)?.promise === promise) {
                this._sampleDropDetailsFetchCache.delete(activityId);
            }
        };
        promise.then(clear, clear);
        return promise;
    }

    private async _fetchSampleDropActivityOnlineImpl(activityId: string): Promise<any | null> {
        try {
            let url = this.authenticationService.userConfig.activeInstance.entryPointUrl +
                Endpoints.activites.GET_ACTIVITY_DETAILS;
            url = url.replace('{activity_id}', activityId);
            url = url.replace('{activity_type}', 'sampledrop');
            url = url.replace('&configFields={{configFields}}', '');
            const options = {
                headers: Endpoints.headers.content_type.json.headers.set('No-Retry', 'true'),
            };
            const response = await this.http.get<any>(url, options).toPromise();
            return response || null;
        } catch (error) {
            console.warn('fetchSampleDropActivityOnline failed:', error);
            return null;
        }
    }

    private async _fetchAndSaveLots(dataRange: { from: string, to: string }, forceFullSync = false, loadFromDbOnly = false) {
        if (!this.deviceService.isOffline && !loadFromDbOnly) {
            const syncState = await this.disk.getSyncState(DB_SYNC_STATE_KEYS.SYNC_LOT);
            const isInitialSync = !syncState || !syncState.lastUpdatedTime;
            const doFullSync = forceFullSync || isInitialSync;
            const newLastUpdatedTime = new Date().getTime();

            const lotSyncInfo: EntitySyncInfo = {
                entityName: EntityNames.lot,
                totalFailed: 0,
                totalSynced: 0,
                errors: [],
                syncStatus: true
            };

            let url = this.authenticationService.userConfig.activeInstance.entryPointUrl +
                Endpoints.sample.LOTS;

            url = url.replace('{{startDate}}', dataRange.from);
            url = url.replace('{{positionIDs}}', this.authenticationService.getLoggedInUserPositions().toString());
            url = this.authenticationService.hasFeatureAction(FeatureActionsMap.ALLOCATION_TRANSFER) ? url + '&features=allocation_transfer' : url;
            url = !doFullSync ? url + '&lastUpdatedTime=' + syncState.lastUpdatedTime : url;

            try {
                const response: LotDTO[] = await this.syncProfilerService.timeAsync('sample_lots_api',
                  () => this.http.get<LotDTO[]>(url, Endpoints.GLOBAL_SYNC_HEADER).toPromise());

                await this.syncProfilerService.timeAsync('sample_lots_processing', async () => {
                  const lots = (response || []).filter(lot => this.isActiveValidOrExpiredLot(lot));

                  if (lots && Array.isArray(lots)) {
                      if (doFullSync) {
                          // Full sync flow
                          if (lots.length > 0) {
                              await this.sampleService.saveFullSyncedLots(lots, forceFullSync);
                          }
                      } else {
                          // Delta sync flow
                          if (lots.length > 0) {
                              this._newDeltaLotIds = [];
                              await this.sampleService.saveDeltaSyncedLots(lots, this._newDeltaLotIds);
                          }
                      }
                      lotSyncInfo.totalSynced = lots.length;
                  }
                  this.sampleService.lotsLastSynTime = newLastUpdatedTime;
                  // Done sync. Update sync state.
                  syncState.lastUpdatedTime = newLastUpdatedTime;
                  await this.disk.updateSyncState(syncState);
                });
            } catch (error) {
                console.error('syncLots: ', error);
                this.deltaService.addSyncErrorToEntitySyncInfo(lotSyncInfo, url, error);
            }

            this.deltaService.addEntitySyncInfo(lotSyncInfo);
        }
    }

    private isActiveValidOrExpiredLot(lot: LotDTO): boolean {
      if (!lot || Number(lot.statecode) !== 0) {
        return false;
      }
      return this.isLotValid(lot) || this.isLotExpired(lot);
    }

    private isLotValid(lot: LotDTO): boolean {
      const now = Date.now();
      const from = Number(lot?.indskr_lotvalidfromdate);
      const to = Number(lot?.indskr_lotvalidtodate);
      if (!Number.isFinite(from) || !Number.isFinite(to)) {
        return false;
      }
      return now >= from && now <= to;
    }

    private isLotExpired(lot: LotDTO): boolean {
      const to = Number(lot?.indskr_lotvalidtodate);
      if (!Number.isFinite(to)) {
        return false;
      }
      return Date.now() > to;
    }

    public async initiateSampleOrder(payload:CreateSampleDropRequestBody){
        //We need to save the payload in the DB/SS until we can resync with backend
        let responseActivity;

        // Manually set activity status to open
        payload['statecode'] = 0;
        payload['activitytypecode'] = 'indskr_sampledrop';
        let current = new Date();
        payload['offlineActivityId'] = 'offline_sampleActivity_'+current.getTime();
        // When created inside a meeting, append the parent meeting's offlineMeetingId (offline_meeting_*)
        // to the sample's own offlineActivityId — always the offlineMeetingId, never the appointment
        // GUID, so the format is stable for offline and online meetings alike. This embeds a durable
        // back-reference to the parent meeting so the appointment link can be recovered even if the
        // two get uploaded separately.
        if (payload['indskr_appointmentid']) {
          let parentOfflineMeetingId = payload['indskr_appointmentid'];
          if (!parentOfflineMeetingId.includes('offline')) {
            // Online meeting: the appointment id is a real GUID, so read the offlineMeetingId off the meeting.
            const parentMeeting: any = this.activityService.getActivityByID(payload['indskr_appointmentid']);
            if (parentMeeting && parentMeeting.offlineMeetingId) {
              parentOfflineMeetingId = parentMeeting.offlineMeetingId;
            }
          }
          payload['offlineActivityId'] = payload['offlineActivityId'] + '_' + parentOfflineMeetingId;
        }
        if (this.deviceService.isOffline || (payload['indskr_appointmentid'] && this.activityService.hasOfflineMeetingData(payload['indskr_appointmentid']))) {
            //Create a new appointment activity with our payload.
            let offlineReqBody = payload;

            let sampleOrderActivity = new SampleActivity(
              offlineReqBody
            );
            sampleOrderActivity.ID = offlineReqBody['offlineActivityId'];
            sampleOrderActivity.ownerId = this.authenticationService.user.systemUserID;

            let rawOfflineSampleOrder = await this.disk.createOfflineSampleOrder(sampleOrderActivity);
            if(!this.activityService.hasOfflineSampleOrderData(rawOfflineSampleOrder.offlineActivityId)){
                this.activityService.addToOfflineSampleOrderIds(rawOfflineSampleOrder.offlineActivityId);
            }
            this.activityService.addActivity(sampleOrderActivity);
            if (!this.uiService.toolsActivityActive){
              this.events.publish('refreshAgenda');
            } else this.uiService.agendaRefreshRequired = true;
            // Update meeting-to-allocation mapping
            if (sampleOrderActivity.appointmentID) {
              this.activityService.samplingToInMeetingActivityMapping.set(
                sampleOrderActivity.appointmentID, sampleOrderActivity.ID
              );
            }
            responseActivity = sampleOrderActivity;
            return responseActivity
        }
        //   call the create sample order service here to create activity in dynamics
           let url: string = this.authenticationService.userConfig.activeInstance.entryPointUrl + Endpoints.sample.CREATE_ALLOCATIONS_DROP;
           let headers = Endpoints.meeting.INITIATE_MEETING_HEADERS;
           headers.headers = headers.headers.set(
             'X-BusinessUnitId',
             this.authenticationService.user.xBusinessUnitId
           ).set(
             "X-PositionId",
             this.authenticationService.user.xPositionID
           ).set(
            "X-SystemUserId",
            this.authenticationService.user.systemUserID
           );

           console.log(`[SAMPLE-DROP] initiateSampleOrder: POSTing indskr_allocationordertype=${payload['indskr_allocationordertype']} appointmentId=${payload['indskr_appointmentid']}`);
           await this.http
             .post(url, payload, headers)
             .toPromise()
             .then(async (response)=>{
                console.log(`[SAMPLE-DROP] initiateSampleOrder: created successfully, id=${response?.['activityid']}`);
                response['statecode'] = 0;
                response['activitytypecode'] = 'indskr_sampledrop'
                let responseAllocationActivity = new SampleActivity(response)
                // POST response may omit fields sent in the request body.
                // Carry over indskr_allocationordertype so isPromoMaterial is correct
                // immediately and the PouchDB record stores the right value.
                if (!responseAllocationActivity.indskr_allocationordertype && payload['indskr_allocationordertype']) {
                  responseAllocationActivity.indskr_allocationordertype = payload['indskr_allocationordertype'];
                }
                await this.disk.updateOrInsert(DB_KEY_PREFIXES.SAMPLE_ACTIVITY + responseAllocationActivity.ID, (doc) => {
                    return responseAllocationActivity;
                });
                this.activityService.addActivity(responseAllocationActivity);
                if (!this.uiService.toolsActivityActive){
                  this.events.publish('refreshAgenda');
                } else this.uiService.agendaRefreshRequired = true;
                // Update meeting-to-allocation mapping
                if (responseAllocationActivity.appointmentID && responseAllocationActivity.statusString == 'Open') {
                  this.activityService.samplingToInMeetingActivityMapping.set(
                    responseAllocationActivity.appointmentID, responseAllocationActivity.ID
                  );
                }
                responseActivity = responseAllocationActivity;
                return
             })
             .catch(async (error)=>{
                // NOTE: this falls back to an OFFLINE record on ANY failure —
                // including a genuine server-side validation rejection (e.g. an
                // invalid indskr_allocationordertype) — so the rep never sees the
                // real error; it just silently creates an offline-pending record
                // instead. Logged here so the actual server response is visible.
                console.error(`[SAMPLE-DROP] initiateSampleOrder: POST failed, falling back to OFFLINE create. error=${JSON.stringify(error?.error ?? error?.message ?? error)}`);
                let offlineReqBody = payload;
                // Manually set activity status to open

                let sampleOrderActivity = new SampleActivity(
                  offlineReqBody
                );
                sampleOrderActivity.ID = offlineReqBody['offlineActivityId'];
                sampleOrderActivity.ownerId = this.authenticationService.user.systemUserID;
                let rawOfflineSampleOrder = await this.disk.createOfflineSampleOrder(
                  sampleOrderActivity
                );
                if(!this.activityService.hasOfflineSampleOrderData(rawOfflineSampleOrder.offlineActivityId)){
                    this.activityService.addToOfflineSampleOrderIds(rawOfflineSampleOrder.offlineActivityId);
                }
                this.activityService.addActivity(sampleOrderActivity)
                if (!this.uiService.toolsActivityActive){
                  this.events.publish('refreshAgenda');
                } else this.uiService.agendaRefreshRequired = true;
                // Update meeting-to-allocation mapping
                if (sampleOrderActivity.appointmentID) {
                  this.activityService.samplingToInMeetingActivityMapping.set(
                    sampleOrderActivity.appointmentID, sampleOrderActivity.ID
                  );
                }
                responseActivity = sampleOrderActivity;
                return
             })
            return responseActivity
    }

    private getCustomerLicenseRecordId(license: any): string {
      return license?.['indskr_customerlicenseid']
        || license?.['indskr_customerlicense.indskr_customerlicenseid']
        || '';
    }

    private getCustomerLicenseContactId(license: any): string {
      return license?.['_indskr_customer_value']
        || license?.['indskr_customer']
        || license?.['indskr_customerlicense._indskr_customer_value']
        || license?.['indskr_customerlicense.indskr_customer']
        || '';
    }

    private isActiveCustomerLicense(license: any): boolean {
      const stateCode = Number(license?.['statecode'] ?? license?.['indskr_customerlicense.statecode']);
      const statusCode = Number(license?.['statuscode'] ?? license?.['indskr_customerlicense.statuscode']);
      return stateCode === 0 && statusCode === 1;
    }

    /** All 4 shapes the state id can appear under in the offline DB. Mirrors
     *  the existing `getCustomerLicenseContactId` helper for contact id. Without
     *  this, licenses written via the master-sync (link-entity prefixed) path
     *  are invisible to the offline matcher and IAO shows false "Invalid". */
    private _getCustomerLicenseStateId(license: any): string {
      return (
        license?.['_indskr_state_value']
        ?? license?.['indskr_state']
        ?? license?.['indskr_customerlicense._indskr_state_value']
        ?? license?.['indskr_customerlicense.indskr_state']
        ?? ''
      );
    }

    private _getCustomerLicenseAddressId(license: any): string {
      return (
        license?.['_indskr_address_value']
        ?? license?.['indskr_address']
        ?? license?.['indskr_customerlicense._indskr_address_value']
        ?? license?.['indskr_customerlicense.indskr_address']
        ?? ''
      );
    }

    private doesLicenseMatchStateAndContact(license: any, stateId: string, contactId: string): boolean {
      // Normalize to lowercase for case-insensitive GUID compare — Dataverse
      // sometimes returns different casing for the same entity via different
      // code paths, which broke strict equality in the previous version.
      const licenseType = this._getCustomerLicenseType(license);
      const licenseStateId = (this._getCustomerLicenseStateId(license) || '').toLowerCase();
      const licenseContactId = (this.getCustomerLicenseContactId(license) || '').toLowerCase();
      const wantStateId = (stateId || '').toLowerCase();
      const wantContactId = (contactId || '').toLowerCase();
      return this.isActiveCustomerLicense(license)
        && licenseType === CUSTOMER_LICENSE_TYPE.State
        && licenseStateId === wantStateId
        && (!wantContactId || !licenseContactId || licenseContactId === wantContactId);
    }

    /** Address-bound license match (for TDDD). TDDD records carry no state
     *  and no customer — the address is the sole linkage to a location. */
    private doesLicenseMatchAddress(license: any, addressId: string): boolean {
      if (!addressId) return false;
      if (!this.isActiveCustomerLicense(license)) return false;
      return (this._getCustomerLicenseAddressId(license) || '').toLowerCase() === addressId.toLowerCase();
    }

    private async loadSampleAbilityLicensesFromDB(): Promise<any[]> {
      // add timer
      const startTime = new Date().getTime();
      const rawDoc = await this.disk.retrieve(DB_KEY_PREFIXES.CUSTOMER_LICENCE_SAMPLE_ABILITY);
      const endTime = new Date().getTime();
      console.log('loadSampleAbilityLicensesFromDB: ', endTime - startTime);
      return Array.isArray(rawDoc?.raw) ? rawDoc.raw : [];
    }

    private async saveSampleAbilityLicensesToDB(licenses: any[]): Promise<void> {
      await this.disk.updateOrInsert(DB_KEY_PREFIXES.CUSTOMER_LICENCE_SAMPLE_ABILITY, doc => ({ raw: licenses }));
    }

    private mergeDeltaSampleAbilityLicenses(existingLicenses: any[], deltaLicenses: any[]): any[] {
      const mergedMap = new Map<string, any>();
      (existingLicenses || []).forEach(license => {
        const id = this.getCustomerLicenseRecordId(license);
        if (id) {
          mergedMap.set(id, license);
        }
      });

      (deltaLicenses || []).forEach(license => {
        const id = this.getCustomerLicenseRecordId(license);
        if (!id) {
          return;
        }
        // Previously this call physically deleted the record whenever a
        // delta row was inactive, which produced a stale "Invalid License"
        // window whenever a license was transiently toggled server-side
        // (edit round-trip, reactivation, etc.) until the next master sync
        // brought it back. Keep the record — the read-side matcher already
        // filters with isActiveCustomerLicense, so an inactive record
        // naturally fails the match without needing to be evicted.
        mergedMap.set(id, license);
      });
      return Array.from(mergedMap.values());
    }

    private mergeCustomerLicensesForContact(existingLicenses: any[], fetchedLicenses: any[], contactId: string): any[] {
      const existingWithoutContactLicenses = (existingLicenses || []).filter(
        license => this.getCustomerLicenseContactId(license) !== contactId
      );
      const activeFetchedLicenses = (fetchedLicenses || []).filter(license => this.isActiveCustomerLicense(license));
      return [...existingWithoutContactLicenses, ...activeFetchedLicenses];
    }

    private isCustomerLicenseCheckEnabled(): boolean {
      return this.authenticationService.hasFeatureAction(FeatureActionsMap.ALLOCATION_ORDERS_UNRESTRICTED)
        || !!(this.authenticationService.hasIoConfigWithFeatureCode(IoConfigurationFeatureCodes.BU_ALLOCATIONORDER_GA_STATELICENSENECESSARYCHECK) as boolean);
    }

    private isStateLicenseNecessaryCheckEnabled(): boolean {
      return !!(this.authenticationService.hasIoConfigWithFeatureCode(IoConfigurationFeatureCodes.BU_ALLOCATIONORDER_GA_STATELICENSENECESSARYCHECK) as boolean);
    }

    private fetchCustomerLicenses(
      contactId?: string,
      lastUpdatedTime?: number,
      isDeltaSync = false,
      forceRefresh = false,
    ): Promise<any[]> {
      if (forceRefresh && contactId) {
        this._customerLicenseFetchCache.delete(contactId);
      }
      const canDedupe = !!contactId && !isDeltaSync && !lastUpdatedTime && !forceRefresh;
      if (canDedupe) {
        const cached = this._customerLicenseFetchCache.get(contactId);
        if (cached && Date.now() < cached.expires) {
          return cached.promise;
        }
      }
      const promise = this._fetchCustomerLicensesImpl(contactId, lastUpdatedTime, isDeltaSync);
      if (canDedupe) {
        this._customerLicenseFetchCache.set(contactId, {
          promise,
          expires: Date.now() + SampleDataService.CUSTOMER_LICENSE_FETCH_TTL_MS,
        });
        promise.catch(() => this._customerLicenseFetchCache.delete(contactId));
      }
      return promise;
    }

    private async _fetchCustomerLicensesImpl(contactId?: string, lastUpdatedTime?: number, isDeltaSync = false): Promise<any[]> {
      try {
      let positionIds = this.authenticationService.getLoggedInUserPositions() || [];
      if (!positionIds.length && this.authenticationService?.user?.xPositionID) {
        positionIds = [this.authenticationService.user.xPositionID];
      }
      const positionValues = positionIds.length
        ? positionIds.map(positionId => `<value>${positionId}</value>`).join('')
        : '<value>00000000-0000-0000-0000-000000000000</value>';
      const deltaTimestamp = isDeltaSync && lastUpdatedTime && !contactId
        ? new Date(lastUpdatedTime).toISOString()
        : '';

      const modifiedOnDeltaFilter = deltaTimestamp
        ? `<filter type="or">
            <condition attribute="modifiedon" operator="on-or-after" value="${deltaTimestamp}" />
            <condition entityname="indskr_customerpositionfilter" attribute="modifiedon" operator="on-or-after" value="${deltaTimestamp}" />
            <condition entityname="contactmodifiedfilter" attribute="modifiedon" operator="on-or-after" value="${deltaTimestamp}" />
            <condition entityname="addressmodifiedfilter" attribute="modifiedon" operator="on-or-after" value="${deltaTimestamp}" />
          </filter>`
        : '';

      const activeOnlyFilter = isDeltaSync ? '' : `
        <condition attribute="statecode" operator="eq" value="0"/>
        <condition attribute="statuscode" operator="eq" value="1"/>`;

      const contactFilter = contactId ? `<condition attribute="indskr_customer" operator="eq" value="${contactId}" />` : '';
      const fetchXML = fetchQueries.fetchSampleAbilityCustomerLicenses
        .replace('{activeOnlyFilter}', activeOnlyFilter)
        .replace('{contactFilter}', contactFilter)
        .replace('{modifiedOnDeltaFilter}', modifiedOnDeltaFilter)
        .replace(/\{positionValues\}/g, positionValues)
        .replace(/\s+/g, ' ');
        const customerLicenses = await this.dynamics.executeFetchQuery('indskr_customerlicenses', fetchXML);
        return Array.isArray(customerLicenses) ? customerLicenses : [];
      } catch (error) {
        console.error('fetchCustomerLicenses: ', error);
        return null;
      }
    }

    public async hasValidStateLicense(
      stateId: string,
      contactId: string,
      options?: { silent?: boolean; loadFromDbOnly?: boolean },
    ): Promise<boolean> {
      if (!this.authenticationService.hasFeatureAction(FeatureActionsMap.ALLOCATION_ORDERS_UNRESTRICTED)) {
        return true;
      }
      const stateLicenseNecessaryCheck = this.authenticationService.getIOConfigurationValue(IoConfigurationFeatureCodes.BU_ALLOCATIONORDER_GA_STATELICENSENECESSARYCHECK);
      if (stateLicenseNecessaryCheck !== "Yes") {
        return true;
      }
      // No stateId → no state license can exist for this address. Skipping
      // the DB find here also prevents the matcher from accidentally returning
      // a TDDD record (whose stateId is '') as a "state license".
      if (!stateId) {
        if (!options?.silent) {
          this.notificationService.notify(
            this.translate.instant('NO_VALID_STATE_LICENSE'),
            'sample-service',
            'top',
            ToastStyle.DANGER
          );
        }
        return false;
      }
      const customerLicensesInDB = await this.loadSampleAbilityLicensesFromDB();
      if (this.deviceService.isOffline || options?.loadFromDbOnly) {
        const offlineLicense = customerLicensesInDB.find(license => this.doesLicenseMatchStateAndContact(license, stateId, contactId));
        if (offlineLicense) {
          return true;
        }
      } else {
        const customerLicenses = await this.fetchCustomerLicenses(contactId);
        if (Array.isArray(customerLicenses)) {
          const mergedLicenses = this.mergeDeltaSampleAbilityLicenses(customerLicensesInDB, customerLicenses);
          await this.saveSampleAbilityLicensesToDB(mergedLicenses);
          const customerLicense = customerLicenses.find(license => this.doesLicenseMatchStateAndContact(license, stateId, contactId));
          if (customerLicense) {
            return true;
          }
        }
      }

      if (!options?.silent) {
        this.notificationService.notify(
          this.translate.instant('NO_VALID_STATE_LICENSE'),
          'sample-service',
          'top',
          ToastStyle.DANGER
        );
      }
      return false;
    }

    private _getCustomerLicenseType(license: any): CUSTOMER_LICENSE_TYPE | null {
      const raw = license?.['indskr_type']
        ?? license?.['indskr_customerlicense.indskr_type'];
      if (raw === null || raw === undefined || raw === '') return null;
      const n = Number(raw);
      return Number.isFinite(n) ? (n as CUSTOMER_LICENSE_TYPE) : null;
    }

    /** Batched DB-only validation — single license read covers many addresses.
     *  Pre-filtering of empty stateIds / addressIds is the caller's choice;
     *  empty values are skipped here and absent from the result maps. */
    public async validateAddressesFromDB(
      inputs: ReadonlyArray<{ stateId?: string; addressId?: string }>,
      contactId: string,
    ): Promise<{
      stateValidByStateId: Map<string, boolean>;
      tdddCategoriesByAddressId: Map<string, Set<CUSTOMER_LICENSE_TYPE>>;
    }> {
      const stateValidByStateId = new Map<string, boolean>();
      const tdddCategoriesByAddressId = new Map<string, Set<CUSTOMER_LICENSE_TYPE>>();
      if (!inputs.length) return { stateValidByStateId, tdddCategoriesByAddressId };

      const licenses = await this.loadSampleAbilityLicensesFromDB();

      for (const input of inputs) {
        const stateId = (input.stateId || '').trim();
        if (stateId && !stateValidByStateId.has(stateId)) {
          const found = licenses.some(l => this.doesLicenseMatchStateAndContact(l, stateId, contactId));
          stateValidByStateId.set(stateId, found);
        }

        const addressId = (input.addressId || '').trim();
        if (addressId && !tdddCategoriesByAddressId.has(addressId)) {
          const cats = new Set<CUSTOMER_LICENSE_TYPE>();
          for (const l of licenses) {
            if (!this.doesLicenseMatchAddress(l, addressId)) continue;
            const t = this._getCustomerLicenseType(l);
            if (isTDDDLicenseType(t)) cats.add(t);
          }
          tdddCategoriesByAddressId.set(addressId, cats);
        }
      }

      return { stateValidByStateId, tdddCategoriesByAddressId };
    }

    public isTDDDLicenseCheckEnabled(): boolean {
      return !!(this.authenticationService.hasIoConfigWithFeatureCode(IoConfigurationFeatureCodes.TDDD_LICENCE_CHECK) as boolean);
    }

    public async getValidTDDDCategoriesForLocation(
      addressId: string,
      options?: { silent?: boolean },
    ): Promise<Set<CUSTOMER_LICENSE_TYPE>> {
      const categories = new Set<CUSTOMER_LICENSE_TYPE>();
      if (!addressId) return categories;

      const customerLicensesInDB = await this.loadSampleAbilityLicensesFromDB();
      for (const license of customerLicensesInDB || []) {
        if (!this.doesLicenseMatchAddress(license, addressId)) continue;
        const type = this._getCustomerLicenseType(license);
        if (isTDDDLicenseType(type)) {
          categories.add(type);
        }
      }

      if (categories.size === 0 && !options?.silent) {
        this.notificationService.notify(
          this.translate.instant('NO_TDDD_LICENSE_FOR_LOCATION'),
          'sample-service',
          'top',
          ToastStyle.DANGER
        );
      }
      return categories;
    }

    private async syncCustomerLicensesSampleAbility(forceFullSync = false, loadFromDbOnly = false) {
      if (!this.isCustomerLicenseCheckEnabled()
        || this.deviceService.isOffline
        || loadFromDbOnly) {
        return;
      }

      const syncState = await this.disk.getSyncState(DB_SYNC_STATE_KEYS.SYNC_CUSTOMER_LICENCE_SAMPLE_ABILITY);
      const isInitialSync = !syncState || !syncState.lastUpdatedTime;
      const doFullSync = forceFullSync || isInitialSync;
      const newLastUpdatedTime = new Date().getTime();
      const licenseSyncInfo: EntitySyncInfo = {
        entityName: EntityNames.customerLicense,
        totalFailed: 0,
        totalSynced: 0,
        errors: [],
        syncStatus: true
      };

      const response = await this.fetchCustomerLicenses(undefined, doFullSync ? undefined : syncState.lastUpdatedTime, !doFullSync);
      if (Array.isArray(response)) {
        const existingLicenses = doFullSync ? [] : await this.loadSampleAbilityLicensesFromDB();
        const mergedLicenses = doFullSync
          ? response.filter(license => this.isActiveCustomerLicense(license))
          : this.mergeDeltaSampleAbilityLicenses(existingLicenses, response);

        await this.saveSampleAbilityLicensesToDB(mergedLicenses);
        if (syncState) {
          syncState.lastUpdatedTime = newLastUpdatedTime;
          await this.disk.updateSyncState(syncState);
        }
        licenseSyncInfo.totalSynced = response.length;
      }

      this.deltaService.addEntitySyncInfo(licenseSyncInfo);
    }

    public async syncCustomerLicensesForContact(
      contactId: string,
      options?: { forceRefresh?: boolean },
    ): Promise<void> {
      if (!contactId
        || !this.isCustomerLicenseCheckEnabled()
        || this.deviceService.isOffline) {
        return;
      }

      const fetchedLicenses = await this.fetchCustomerLicenses(
        contactId,
        undefined,
        false,
        !!options?.forceRefresh,
      );
      if (!Array.isArray(fetchedLicenses)) {
        return;
      }

      const existingLicenses = await this.loadSampleAbilityLicensesFromDB();
      const mergedLicenses = this.mergeCustomerLicensesForContact(existingLicenses, fetchedLicenses, contactId);
      const activeFetchedLicenses = fetchedLicenses.filter(license => this.isActiveCustomerLicense(license));
      await this.saveSampleAbilityLicensesToDB(mergedLicenses);

      if (this.isStateLicenseNecessaryCheckEnabled()) {
        if (activeFetchedLicenses.length > 0) {
          this.eligibleCustomerLicenseContactIds.add(contactId);
        } else {
          this.eligibleCustomerLicenseContactIds.delete(contactId);
        }
      }
    }

    public async refreshEligibleCustomerLicenseContactsCache(): Promise<void> {
      try {
        this.eligibleCustomerLicenseContactIds.clear();
        if (!this.isStateLicenseNecessaryCheckEnabled()) {
          return;
        }
        const licenses = await this.loadSampleAbilityLicensesFromDB();
        licenses.forEach(license => {
          if (this.isActiveCustomerLicense(license)) {
            const contactId = this.getCustomerLicenseContactId(license);
            if (contactId) {
              this.eligibleCustomerLicenseContactIds.add(contactId);
            }
          }
        });
      } catch (error) {
        console.error('refreshEligibleCustomerLicenseContactsCache: ', error);
      }
    }

    public hasEligibleCustomerLicenseForContact(contactId: string): boolean {
      return this.isStateLicenseNecessaryCheckEnabled() && !!contactId && this.eligibleCustomerLicenseContactIds.has(contactId);
    }

  private updateCustomerLicenseInDB(customerLicensesInDB: any, license: any) {
    const index = customerLicensesInDB.raw.findIndex(licenseDB => {
      return licenseDB['indskr_customerlicense.indskr_customerlicenseid'] === license['indskr_customerlicenseid'];
    });
    if (index >= 0) {
      // Update only matching keys from license into customerLicensesInDB.raw[index]
      const existingLicense = customerLicensesInDB.raw[index];
      const odataFormattedSuffix = '@OData.Community.Display.V1.FormattedValue';
      const formattedSuffix = '_Formatted';

      // Build an index of existingLicense keys for O(1) lookup
      // Map structure: { baseKeyName: { suffixType: existingKey } }
      // suffixType: 'regular' | 'odata' | 'formatted'
      const existingKeysIndex = new Map<string, Map<string, string>>();

      for (const existingKey of Object.keys(existingLicense)) {
        const hasODataSuffix = existingKey.includes(odataFormattedSuffix);
        const hasFormattedSuffix = existingKey.includes(formattedSuffix);
        let baseKeyName = existingKey;
        let suffixType = 'regular';

        if (hasODataSuffix) {
          baseKeyName = existingKey.split(odataFormattedSuffix)[0];
          suffixType = 'odata';
        } else if (hasFormattedSuffix) {
          baseKeyName = existingKey.split(formattedSuffix)[0];
          suffixType = 'formatted';
        }

        // Handle keys with underscore prefix pattern
        if (baseKeyName.startsWith('_') && baseKeyName.endsWith('_value')) {
          baseKeyName = baseKeyName.substring(1).replace(/_value$/, '');
        }

        // Extract the actual field name from keys like 'indskr_customerlicense.indskr_state'
        const fieldNameMatch = baseKeyName.match(/\.([^.]+)$/);
        const fieldName = fieldNameMatch ? fieldNameMatch[1] : baseKeyName;

        if (!existingKeysIndex.has(fieldName)) {
          existingKeysIndex.set(fieldName, new Map());
        }
        existingKeysIndex.get(fieldName)!.set(suffixType, existingKey);
      }

      // Process license keys and update using the index
      for (const licenseKey of Object.keys(license)) {
        const hasODataFormattedSuffix = licenseKey.includes(odataFormattedSuffix);
        const hasFormattedSuffix = licenseKey.includes(formattedSuffix);
        let baseKeyName = licenseKey;
        let suffixType = 'regular';

        // Remove formatted suffix if present
        if (hasODataFormattedSuffix) {
          baseKeyName = licenseKey.split(odataFormattedSuffix)[0];
          suffixType = 'odata';
        } else if (hasFormattedSuffix) {
          baseKeyName = licenseKey.split(formattedSuffix)[0];
          suffixType = 'formatted';
        }

        // Handle keys with underscore prefix pattern: _<fieldname>_value
        if (baseKeyName.startsWith('_') && baseKeyName.endsWith('_value')) {
          baseKeyName = baseKeyName.substring(1).replace(/_value$/, '');
        }

        // Extract the actual field name (same logic as for existing keys)
        const fieldNameMatch = baseKeyName.match(/\.([^.]+)$/);
        const fieldName = fieldNameMatch ? fieldNameMatch[1] : baseKeyName;

        // Look up matching key in index
        const matchingKeys = existingKeysIndex.get(fieldName);
        if (matchingKeys) {
          const matchingKey = matchingKeys.get(suffixType);
          if (matchingKey) {
            existingLicense[matchingKey] = license[licenseKey];
          }
        }
      }
    }
  }

    public async updateSampleActivity(payload:CreateSampleDropRequestBody, sampleActivity?: SampleActivity, contact?: Contact):Promise<any>{
        let updatedActivity;
        if (payload && sampleActivity && payload.indskr_signature !== undefined && payload.indskr_signature !== null) {
          sampleActivity.signature = payload.indskr_signature;
        }
        if (this.deviceService.isOffline || sampleActivity.ID.includes('offline') || this.activityService.hasOfflineSampleOrderData(sampleActivity.offlineActivityId)) {
            //update local db for this activity
            this.disk.updateOrInsertActivityToActivityDetailRawDocument(sampleActivity, true)
            // let localSavedActivity =
            // let rawOfflineSampleOrder = await this.disk.createOfflineSampleOrder(
            //   sampleOrderActivity
            // );

            //add to offline update document hashmap
            let id_to_be_updated:string =  sampleActivity.offlineActivityId;
            if(!this.activityService.hasOfflineSampleOrderData(id_to_be_updated)){
                this.activityService.addToOfflineSampleOrderIds(id_to_be_updated);
                await this.disk.createOfflineSampleOrder(sampleActivity)
            }
            //update offline upload document for this activity
            else(
                this.disk.updateofflineSampleOrderDocument(sampleActivity)
            )
            //add/update to session display activities
            if(sampleActivity.state == 2){
                await this.activityService.removeActivity(sampleActivity);
                if(sampleActivity.appointmentID && sampleActivity.appointmentID.length > 0)
                    this.events.publish('deletedEmbeddedActivity', sampleActivity);
            }
            else{
                await this.activityService.addActivity(sampleActivity, true);
            }
            this.activityService.publishActivityEvent({action: "Update", activity: sampleActivity});

            if (sampleActivity.appointmentID && sampleActivity.appointmentID.length > 0 && sampleActivity.state === 1) {
                this.events.publish('updateEmbeddedActivity', sampleActivity);
            }
            // interaction for contact for completed sample order
            if(sampleActivity.status === 2){
              await this._updateInteractionForContacts(contact);
            }
            //Publish event to update the agenda list
            if (!this.uiService.toolsActivityActive){
              this.events.publish('refreshAgenda');
            } else this.uiService.agendaRefreshRequired = true;
            //responseActivity = sampleOrderActivity;
            //Publish event to update the completed allocation order in contact timeline offline mode
            if(this.deviceService.isOffline && sampleActivity.status === 2) {
              await this.activityService.publishOfflineActivityForTimeline(sampleActivity);
            }
            return // responseActivity
        }
        else{
            let url: string = this.authenticationService.userConfig.activeInstance.entryPointUrl + Endpoints.sample.UPDATE_ALLOCATIONS_DROP;
            if(payload.activityid){
                url = url.replace('{{SampleActivityId}}', payload.activityid)
            }
            else{
                //add to offline update document hashmap
                if(!this.activityService.hasOfflineSampleOrderData(sampleActivity.offlineActivityId)){
                    this.activityService.addToOfflineSampleOrderIds(sampleActivity.offlineActivityId);
                    this.disk.createOfflineSampleOrder(sampleActivity)
                }
                //update offline upload document for this activity
                else(
                    this.disk.updateofflineSampleOrderDocument(sampleActivity)
                )

                //add/update to session display activities
                if(sampleActivity.state == 2){
                    await this.activityService.removeActivity(sampleActivity);
                    if(sampleActivity.appointmentID && sampleActivity.appointmentID.length > 0)
                        this.events.publish('deletedEmbeddedActivity', sampleActivity);
                }
                else{
                    await this.activityService.addActivity(sampleActivity, true);
                }

                if (sampleActivity.appointmentID && sampleActivity.appointmentID.length > 0 && sampleActivity.state === 1) {
                    this.events.publish('updateEmbeddedActivity', sampleActivity);
                }
                // interaction for contact for completed sample order
                if (sampleActivity.status === 2) {

                  await this._updateInteractionForContacts(contact);
                }
                //Publish event to update the agenda list
                if (!this.uiService.toolsActivityActive){
                  this.events.publish('refreshAgenda');
                } else this.uiService.agendaRefreshRequired = true;
                //responseActivity = sampleOrderActivity;
                return // responseActivity
            }
            let headers = Endpoints.meeting.INITIATE_MEETING_HEADERS;
            headers.headers = headers.headers.set(
             'X-BusinessUnitId',
             this.authenticationService.user.xBusinessUnitId
            ).set(
             "X-PositionId",
             this.authenticationService.user.xPositionID
            ).set(
                "X-SystemUserId",
                this.authenticationService.user.systemUserID
            );
            await this.http
             .patch(url, payload, headers)
             .toPromise()
             .then(async ()=>{
                // add to session display activities
                if(sampleActivity.state == 2){
                    this.activityService.removeActivity(sampleActivity)
                    if(sampleActivity.appointmentID && sampleActivity.appointmentID.length > 0)
                        this.events.publish('deletedEmbeddedActivity', sampleActivity);
                }
                else{
                    await this.activityService.addActivity(sampleActivity, true);
                }
                 // interaction for contact for completed sample order
                 if (sampleActivity.status === 2) {
                  await this._updateInteractionForContacts(contact);
                 }
             
                if (sampleActivity.appointmentID && sampleActivity.appointmentID.length > 0 && sampleActivity.state === 1) {
                    this.events.publish('updateEmbeddedActivity', sampleActivity);
                }
                //UPSERT LOCAL DISK RAW ACTIVITY
                this.disk.updateOrInsertActivityToActivityDetailRawDocument(sampleActivity, true)
                //publish event to update agenda list
                this.activityService.publishActivityEvent({action: "Update", activity: sampleActivity});
                this.activityService.finalizeAgendaRefresh();
              return;
             })
             .catch(async (error)=>{
                 let errorInfo = (error.hasOwnProperty('error')) ? error.error : error;
                if (errorInfo.errorCode == 'ERR_IO_SR01' || errorInfo.errorCode == 'ERR_IO_SR07') {
                    //for some reason, limits have exceeded for one of the SKUs in this sample activity
                    this.notificationService.notify(this.translate.instant('EXCEEDED_ALLOWCATION_LIMIT'),'Sample Data Service','top');
                    this.sampleService.updateSampleEligibilities(sampleActivity,errorInfo['errorDetails']);
                    throw new Error('exceededLimits');
                }
                if (errorInfo.errorCode == 'ERR_IO_SR08') {
                  this.notificationService.notify(this.translate.instant('CUSTOMER_DROP_LIMIT_EXEED_ERROR'), 'Sample Data Service', 'top', ToastStyle.DANGER);
                  throw new Error('exceededLimits');
                }
                if(errorInfo.errorCode =='ERR_IO_SR04'){
                    this.notificationService.notify(this.translate.instant('ALLOWCATION_COULD_NOT_BE_COMPLETED'),'Sample Data Service','top');
                    //this.sampleService.updateSampleEligibilities(sampleActivity,errorInfo['errorDetails']);
                    throw new Error('allocation recalled');
                }
                if(errorInfo.errorCode == "ERR_IO_500"){
                    this.notificationService.notify(this.translate.instant('NETWORK_ISSUES_WHILE_COMPLETE_REQUEST'),'Sample Data Service','top',ToastStyle.DANGER);
                    throw new Error('Server Error');
                }
                this.disk.updateOrInsertActivityToActivityDetailRawDocument(sampleActivity, true)
          
                //add to offline update document hashmap
                if(!this.activityService.hasOfflineSampleOrderData(sampleActivity.offlineActivityId)){
                    this.activityService.addToOfflineSampleOrderIds(sampleActivity.offlineActivityId);
                    this.disk.createOfflineSampleOrder(sampleActivity)
                }
                //update offline upload document for this activity
                else(
                    this.disk.updateofflineSampleOrderDocument(sampleActivity)
                )

                //add/update to session display activities
                if(sampleActivity.state == 2){
                    await this.activityService.removeActivity(sampleActivity);
                    if(sampleActivity.appointmentID && sampleActivity.appointmentID.length > 0)
                        this.events.publish('deletedEmbeddedActivity', sampleActivity);
                }
                else{
                    await this.activityService.addActivity(sampleActivity, true);
                }

                if (sampleActivity.appointmentID && sampleActivity.appointmentID.length > 0 && sampleActivity.state === 1) {
                    this.events.publish('updateEmbeddedActivity', sampleActivity);
                }
                //Publish event to update the agenda list
                this.activityService.publishActivityEvent({action: "Update", activity: sampleActivity});
                if (!this.uiService.toolsActivityActive){
                  this.events.publish('refreshAgenda');
                } else this.uiService.agendaRefreshRequired = true;
                return // responseActivity
             })
             return updatedActivity
        }
    }

    /**
 * Updates the interaction information for a specific contact in the local database.
 * 
 * This method performs the following steps:
 * 1. Determines the appropriate bucket ID for the contact using the contact's ID and the number of buckets.
 * 2. Constructs the database key for storing the contact data by prefixing with the appropriate key.
 * 3. Retrieves the existing contacts data from the local storage using the generated database key.
 * 4. If data exists and is an array, it assigns it to `dbContacts`; otherwise, initializes `dbContacts` as an empty array.
 * 5. Updates the interaction information of the provided contact within the `dbContacts` array, specifically updating the 'Allocations' field.
 * 6. If `dbContacts` is not empty after the update, it stores the updated contact information back in the local storage by either updating or inserting the document with the modified contact list.
 * 
 * @param contact - The contact object that needs to be updated with new interaction information.
 * @returns Promise<void>
 */
  private async _updateInteractionForContacts(contact: Contact) {
    const bucketID = getBucketId(contact.ID, this.authenticationService.get_num_buckets());

    const dbKey = Utility.getDBBucketedKey(DB_KEY_PREFIXES.CONTACT, bucketID);

    let dbContacts = [];

    let rawContactsDoc: any = await this.disk.retrieve(dbKey);

    if (rawContactsDoc && rawContactsDoc.raw && Array.isArray(rawContactsDoc.raw)) {

      dbContacts = rawContactsDoc.raw;
    }

    dbContacts = this.globalUtility.updateInteractionContact(contact, 'Allocations', dbContacts);

    if (!isEmpty(dbContacts)) {
      this.disk.updateOrInsert(dbKey, doc => ({ raw: dbContacts }));
    }
  }

  /**
 * Synchronizes customer licenses with the server or loads them from the local database, depending on the device's online status and the provided flags.
 * 
 * The method operates in two main modes:
 * 1. **Online Mode (Default):** If the device is online and `loadFromDbOnly` is `false`, it performs a synchronization of customer licenses:
 *    - **Full Sync:** If `forceFullSync` is `true` or it's the first sync (no previous sync state found), it fetches all customer licenses from the server.
 *    - **Delta Sync:** If a previous sync state exists and `forceFullSync` is `false`, it fetches only the updated customer licenses since the last sync.
 *    - The method constructs the appropriate API URL based on the sync mode (full or delta).
 *    - The retrieved data is then processed:
 *      - **Full Sync:** Maps all customer licenses from the server to the local storage.
 *      - **Delta Sync:** Compares and updates the local storage with any changes from the server.
 *    - Finally, it updates the sync state with the current timestamp and records the sync operation's details (total synced, errors, etc.).
 * 
 * 2. **Offline Mode or Load from DB:** If the device is offline or `loadFromDbOnly` is `true`, it skips the server sync and directly loads the customer licenses from the local database, mapping them for use.
 * 
 * The method handles errors during the sync process, logs them, and updates the sync information accordingly.
 * 
 * @param forceFullSync - If `true`, forces a full sync, ignoring the last sync time.
 * @param loadFromDbOnly - If `true`, skips the server sync and loads data only from the local database.
 * @returns Promise<void>
 */
    async syncCustomerLicenses(forceFullSync = false, loadFromDbOnly = false, profilerParentSpanId?: string) {
      console.log('syncCustomerLicenses', forceFullSync, loadFromDbOnly);
        await this.syncProfilerService.timeAsync('customer_licenses_sample_ability', () => this.syncCustomerLicensesSampleAbility(forceFullSync, loadFromDbOnly), profilerParentSpanId);

        if (!(this.deviceService.isOffline || loadFromDbOnly)) {
            const syncState = await this.disk.getSyncState(DB_SYNC_STATE_KEYS.SYNC_CUSTOMER_LICENSES);
            const isInitialSync = !syncState || !syncState.lastUpdatedTime;
            const doFullSync = forceFullSync || isInitialSync;
            const newLastUpdatedTime = new Date().getTime();

            let url: string = this.authenticationService.userConfig.activeInstance.entryPointUrl + Endpoints.sample.GET_CUSTOMER_LICENSES;
            url = url.replace('{{positionIDs}}',this.authenticationService.getLoggedInUserPositions().toString())
            url = !doFullSync ? url + '&lastUpdatedTime=' + syncState.lastUpdatedTime : url;

            const licenseSyncInfo: EntitySyncInfo = {
                entityName: EntityNames.customerLicense,
                totalFailed: 0,
                totalSynced: 0,
                errors: [],
                syncStatus: true
            };

            let response: CustomerLicenseDTO[];
            try {
                response = await this.syncProfilerService.timeAsync('customer_licenses_api',
                  () => this.http.get<CustomerLicenseDTO[]>(url, Endpoints.GLOBAL_SYNC_HEADER).toPromise(),
                  profilerParentSpanId);
            } catch (error) {
                console.error('syncLicenses: ', error);
                this.deltaService.addSyncErrorToEntitySyncInfo(licenseSyncInfo, url, error);
            }

            if (response && Array.isArray(response)) {
                await this.syncProfilerService.timeAsync('customer_licenses_processing', async () => {
                  if (doFullSync) {
                      // Initial full sync flow
                      await this.sampleService.mapFullSyncedCustomerLicenses(response, newLastUpdatedTime, forceFullSync);
                      this._isInitialMappingDone = true;
                  } else {
                      // Delta sync flow
                      const dbCustomerLicenses = await this.sampleService.loadCustomerLicensesFromDB();

                      if (!this._isInitialMappingDone) {
                          await this.sampleService.mapCustomerLicensesWithDbData(dbCustomerLicenses);
                          this._isInitialMappingDone = true;
                      }

                      // Map delta
                      await this.sampleService.mapDeltaSyncedCustomerLicenses(response, dbCustomerLicenses, newLastUpdatedTime);
                  }
                }, profilerParentSpanId);

                // Done sync. Update sync state.
                if (licenseSyncInfo.syncStatus) {
                    syncState.lastUpdatedTime = newLastUpdatedTime;
                    licenseSyncInfo.totalSynced = response.length;
                    await this.disk.updateSyncState(syncState);
                }
            }

            this.deltaService.addEntitySyncInfo(licenseSyncInfo);
        } else {
            // Just load data from DB and map.
            const dbCustomerLicenses = await this.sampleService.loadCustomerLicensesFromDB();
            await this.sampleService.mapCustomerLicensesWithDbData(dbCustomerLicenses);
        }
    }
}
